'use client'

import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react'

import { Drawer } from '@/components/atoms'

import type { DrawerKey, DrawerPayloads } from './drawer.type'
import { drawerRegistry } from './drawer.registry'
import { setDrawerOpenHandler } from './drawer.registry.events'

type DrawerStateForKey<K extends DrawerKey> = {
  key: K
  payload: DrawerPayloads[K]
}

type DrawerState =
  | {
      [K in DrawerKey]: DrawerStateForKey<K>
    }[DrawerKey]
  | null

type DrawerRegistryContextValue = {
  openDrawer: <K extends DrawerKey>(key: K, payload: DrawerPayloads[K]) => void
  close: () => void
  setSuspended: (key: DrawerKey, suspended: boolean) => void
  isOpen: boolean
  isSuspended: boolean
  activeKey: DrawerKey | null
}

const DrawerRegistryContext = createContext<DrawerRegistryContextValue | null>(
  null,
)

export function DrawerRegistryProvider({
  children,
  respectAppHeader = false,
}: {
  children: React.ReactNode
  respectAppHeader?: boolean
}) {
  const [state, setState] = useState<DrawerState>(null)
  const [suspendedKey, setSuspendedKey] = useState<DrawerKey | null>(null)

  const openDrawer = useCallback(
    <K extends DrawerKey>(key: K, payload: DrawerPayloads[K]) => {
      if (suspendedKey !== null) return
      setState({ key, payload } as DrawerState)
    },
    [suspendedKey],
  )

  const close = useCallback(() => {
    setSuspendedKey(null)
    setState(null)
  }, [])

  const setSuspended = useCallback((key: DrawerKey, suspended: boolean) => {
    setSuspendedKey((current) =>
      suspended ? key : current === key ? null : current,
    )
  }, [])

  // expose an imperative open for non-React callers (e.g., menu functions)
  React.useEffect(() => {
    setDrawerOpenHandler(openDrawer)
    return () => setDrawerOpenHandler(undefined)
  }, [openDrawer])

  const hasDrawer = Boolean(state)
  const isSuspended = state?.key === suspendedKey
  const isOpen = hasDrawer && !isSuspended
  const commonPayload = state?.payload

  const value = useMemo<DrawerRegistryContextValue>(
    () => ({
      openDrawer,
      close,
      setSuspended,
      isOpen,
      isSuspended,
      activeKey: state?.key ?? null,
    }),
    [openDrawer, close, isOpen, isSuspended, setSuspended, state?.key],
  )

  function renderFromRegistry<K extends DrawerKey>(
    key: K,
    payload: DrawerPayloads[K],
  ) {
    const { render } = drawerRegistry[key]
    return render({ ...payload, hideDrawer: close })
  }

  return (
    <DrawerRegistryContext.Provider value={value}>
      {children}
      {state ? (
        <Drawer
          open={hasDrawer}
          setOpen={(o) => {
            if (!o) close()
          }}
          title={commonPayload?.title}
          mask={commonPayload?.mask}
          panelSize={commonPayload?.panelSize}
          className={commonPayload?.className}
          respectAppHeader={respectAppHeader}
          suspended={isSuspended}
        >
          {renderFromRegistry(state.key, state.payload)}
        </Drawer>
      ) : null}
    </DrawerRegistryContext.Provider>
  )
}

export function useDrawerRegistry() {
  const ctx = useContext(DrawerRegistryContext)
  if (!ctx)
    throw new Error(
      'useDrawerRegistry must be used within DrawerRegistryProvider',
    )
  return ctx
}
